import { z } from "zod";
import { sameRef, sameScope, type Diagnostic, type Ref, type Scope } from "../work-os/primitives.ts";
import { SurfacePublication, type SurfaceDraft } from "./lifecycle.ts";
import { SurfacePublishIntent } from "./spec.ts";

// Chỉ kiểm precondition trên snapshot canonical. W.5 vẫn cần owner authorization,
// CAS pointer + consume confirmation + audit/outbox trong cùng transaction.
export function checkSurfacePublish(intent: z.infer<typeof SurfacePublishIntent>, current: {
  scope: Scope; bindingId: string; activeVersion: number; draft: SurfaceDraft; compiledDigest: string;
  compilationValid: boolean; dependenciesReady: boolean; now: string;
  confirmation: {
    ref: Ref; bindingId: string; scope: Scope; draftRef: Ref; compiledDigest: string; expectedBaseVersion: number;
    expiresAt: string; authorityValid: boolean; consumed: boolean;
  };
}): Diagnostic[] {
  const errors: Diagnostic[] = [];
  const add = (code: string) => errors.push({ code, path: "publish" });
  if (intent.workspaceId !== current.scope.workspaceId || !sameScope(current.scope, current.draft.scope) ||
    !sameScope(current.scope, current.draft.definition.scope) || !sameScope(current.scope, current.confirmation.scope)) add("SCOPE_MISMATCH");
  if (intent.bindingId !== current.bindingId || current.draft.bindingId !== current.bindingId || current.confirmation.bindingId !== current.bindingId)
    add("BINDING_MISMATCH");
  if (intent.expectedBaseVersion !== current.activeVersion || current.draft.expectedBaseVersion !== current.activeVersion ||
    current.confirmation.expectedBaseVersion !== current.activeVersion || intent.draftRef.id !== current.draft.id || intent.draftRef.version !== current.draft.revision ||
    !sameRef(intent.draftRef, current.confirmation.draftRef)) add("VERSION_CONFLICT");
  if (intent.compiledDigest !== current.compiledDigest || intent.compiledDigest !== current.confirmation.compiledDigest) add("DIGEST_MISMATCH");
  if (!current.compilationValid || !current.dependenciesReady) add("DEPENDENCY_UNAVAILABLE");
  if (!sameRef(intent.confirmationRef, current.confirmation.ref) || !current.confirmation.authorityValid || current.confirmation.consumed ||
    !Number.isFinite(Date.parse(current.now)) || !Number.isFinite(Date.parse(current.confirmation.expiresAt)) ||
    Date.parse(current.now) >= Date.parse(current.confirmation.expiresAt)) add("CONFIRMATION_INVALID");
  return errors;
}

/** Shared list frame check at owner/BFF/SDK boundaries; no manifest or data authority is inferred. */
export function validSurfacePublicationPage(data:import("./lifecycle.ts").SurfacePublication[],page:{hasMore:boolean;nextCursor:string|null},scope:Scope,query:import("./lifecycle.ts").SurfacePublicationListQuery):boolean {
 if(data.length>query.limit||page.hasMore!==(page.nextCursor!==null)||page.hasMore&&data.length!==query.limit)return false;
 let previous=query.cursor?.toLowerCase()??"";
 for(const p of data){
  if(!SurfacePublication.safeParse(p).success)return false;
  if(!sameScope(p.scope,scope)||!sameScope(p.navigation.scope,scope)||p.navigation.id!==p.bindingId||p.navigation.version!==p.revision||p.releaseRef.id!==p.bindingId||p.releaseRef.version!==p.revision||p.bindingId<=previous||p.navigation.entries.some(e=>!p.surfaceVersionRefs.some(r=>sameRef(r,e.surfaceRef))))return false;
  previous=p.bindingId;
 }
 return !page.hasMore||page.nextCursor===previous;
}
